import {Mental} from "../../Mental";
import {MentalNormalSymbol} from "../../reel/symbols/MentalNormalSymbol";
import {ReelModelSymbolData} from "../../../framework/reel/ReelModel";
import {MentalSymbolStates} from "../../reel/MentalSymbol";
import {NoWinPresentation} from "../../noWin/NoWinPresentation";
import {ReelEnhancer} from "../../server/ReelEnhancerData";

export class XFeaturePresentation {
    /**
     * function to play xWays feature animation
     * `reelId`,`symbolId` to get symbol position
     * then split function will be called to add split symbols of symbolName
     */
    public static playXWaysFeaturePresentation(reelId: number, symbolId: number, symbolName: string[], symbolSize: number, fromSplit: boolean, canSkipWildAnim: boolean = false): void {
        const symbolData: ReelModelSymbolData = Mental.reelArea.getReel(reelId).model.getReelSymbolAt(symbolId);
        const symbol: MentalNormalSymbol = <MentalNormalSymbol>symbolData.positionable;

        if (symbolName.includes("D") || symbolName.includes("DG")) { // #299, check updated, trigger the cheat from #299
            // to fix #314
            if (Mental.deadFeaturePresentation.getDeadCount() > 1 || symbolName.filter((value: string) => value === "D").length > 1) {
                symbolName = symbolName.map((item: string) => {
                    if (item.length === 1) { // if get DG not to make it DGG
                        return item.replace("D", "DG");
                    } else {
                        return item;
                    }
                });
                Mental.deadFeaturePresentation.playActiveAnim(reelId, symbolId, true);
            } else {
                // to fix #329
                if (Mental.deadFeaturePresentation.isS2Transform(reelId, symbolId) && (Mental.deadFeaturePresentation.getDeadCount() > 1 || (symbolName.length === 1 && symbolName[0] === "D" && symbolSize === 2))) {
                    Mental.deadFeaturePresentation.tryDeadToGoldOnFeatureReel(reelId, symbolId, true);
                } else {
                    Mental.deadFeaturePresentation.tryDeadToGoldOnNormalReels(reelId, true);
                }
            }
        }
        const multi: number[] = Mental.model.gameData.deadMultiplierPlacedValue[reelId][symbolId];
        if (symbolName.length > 1) {
            symbol.splitInMultiSymbols(symbolSize, symbolName, multi);
        } else {
            symbol.split(symbolSize, symbolName[0], symbolSize, multi);
        }
        symbol.allowedToResizeSegments = true;

        if (fromSplit) {
            symbol.setStateAsPerParts(symbolSize, canSkipWildAnim);
        } else {
            symbol.setState(MentalSymbolStates.NONE);
        }

        if (symbolName[0] === "S" && (reelId === 0 || reelId === 2 || reelId === 4)) {
            NoWinPresentation.tryDimmingScatter(reelId, true);
        }

        // To fix #272
        XFeaturePresentation.showMultiplierOnSymbol(reelId, symbolId, symbol);
    }

    /**
     * function to play only xSplit animation on enhancer
     * @param reelId
     * @param symbolId
     * @param symbolName
     * @param canSkipWildAnim
     */
    public static playXSplitForEnhancer(reelId: number, symbolId: number, symbolName: string[], canSkipWildAnim: boolean = false): void {
        // since enhancer-split runs in the end safe to use finalSymbolSize here.
        let symbolSize: number = Mental.model.gameData.finalSymbolSize[reelId][symbolId];

        // case : one enhancer has MID? WILD? DEAD? and other one has xSplit both should split within enhancer size
        if (Mental.reelArea.getReel(reelId).isSpecialSymbolReel && symbolId === 2 && symbolSize === 2) {
            symbolSize = 4;
        }
        const symbolData: ReelModelSymbolData = Mental.reelArea.getReel(reelId).model.getStopSymbolAt(symbolId);
        const symbol: MentalNormalSymbol = <MentalNormalSymbol>symbolData.positionable;

        if (symbolName.includes("D") || symbolName.includes("DG")) { // #415, condition modified to check for "DG" symbol too, S2 can convert to D and after that if total D gets more than 1 then all D goes to DG, hence we need to check for DG also.
            if (Mental.deadFeaturePresentation.getDeadCount() > 1) {
                const reelsToCheck: number[] = [0, 1, 2, 3, 4];

                for (let i: number = 0; i < reelsToCheck.length; i++) {
                    let currDeadPos: number[] = Mental.deadFeaturePresentation.getFinalDeadPositions(reelsToCheck[i]);

                    for (let j: number = 0; j < currDeadPos.length; j++) {
                        if (currDeadPos[j] > 0) {
                            if (!(symbolName.includes("DG"))) {
                                symbolName = symbolName.map((item: string) => {
                                    if (item.length === 1) { // if get DG not to make it DGG
                                        return item.replace("D", "DG");
                                    } else {
                                        return item;
                                    }
                                });
                            }
                            Mental.deadFeaturePresentation.playActiveAnim(i, j, true);
                        }
                    }
                }
            } else {
                if (Mental.deadFeaturePresentation.isOnEnhancer(reelId, symbolId)) {
                    Mental.deadFeaturePresentation.tryDeadToGoldOnEnhancerCell(reelId, true);
                } else {
                    if (!Mental.deadFeaturePresentation.isS2Transform(reelId, symbolId)) {
                        Mental.deadFeaturePresentation.tryDeadToGoldOnNormalReels(reelId, true);
                    }
                }
            }
        }
        const multi: number[] = Mental.model.gameData.deadMultiplierPlacedValue[reelId][symbolId];

        if (symbolName.length > 1) {
            symbol.splitInMultiSymbols(symbolSize, symbolName, multi);
        } else {
            symbol.split(symbolSize, symbolName[0], symbolSize, multi);
        }
        symbol.allowedToResizeSegments = true;
        symbol.setStateAsPerParts(symbolSize, canSkipWildAnim);

        // To fix #272
        XFeaturePresentation.showMultiplierOnSymbol(reelId, symbolId, symbol);
    }

    /**
     * function to ply only xWays presentation on enhancer
     * @param reelId
     * @param symbolId
     * @param symbolName
     */
    public static playXWaysForEnhancer(reelId: number, symbolId: number, symbolName: string[]): void {
        let symbolSize: number = Mental.model.gameData.currentSymbolSize[reelId][symbolId];
        const symbolData: ReelModelSymbolData = Mental.reelArea.getReel(reelId).model.getStopSymbolAt(symbolId);
        const symbol: MentalNormalSymbol = <MentalNormalSymbol>symbolData.positionable;
        symbol.split(symbolSize, symbolName[0], symbolSize);
        symbol.allowedToResizeSegments = true;
        symbol.setStateAsPerParts(symbolSize);
    }

    // #272
    private static showMultiplierOnSymbol(reelId: number, symbolId: number, symbol: MentalNormalSymbol): void {
        if (symbol._splitSymbols.length && Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symbolId].includes(true)) {
            let checkForXWays: boolean = false,
                xWaysIndexes: number[] = [];
            if (Mental.model.gameData.scatter2Position[reelId] === symbolId) {
                const features: string[] = (reelId === 1 ? Mental.model.gameData.firstS2FeatureTriggered : Mental.model.gameData.secondS2FeatureTriggered),
                    featureSize: number[] = (reelId === 1 ? Mental.model.gameData.firstS2FeatureSize : Mental.model.gameData.secondS2FeatureSize);
                if (features.includes(ReelEnhancer.XWAYS)) {
                    checkForXWays = true;

                    let startIdx: number = 0;
                    for (let i: number = 0; i < features.length; i++) {
                        if (symbol._splitSymbols[startIdx] && symbol._splitSymbols[startIdx].symbolName === "S2") {
                            startIdx += 1;
                        } else {
                            if (features[i] === ReelEnhancer.XWAYS) {
                                for (let j: number = 0; j < featureSize[i]; j++) {
                                    xWaysIndexes.push(startIdx + j);
                                }
                            }
                            startIdx += featureSize[i];
                        }
                    }
                }
            }
            for (let i: number = 0; i < symbol._splitSymbols.length; i++) {
                // #429 => https://github.com/nolimitcity/mental/issues/429#issuecomment-2782649038
                if (checkForXWays && xWaysIndexes.includes(i)) {
                    continue;
                }

                if (Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symbolId][i]) {
                    symbol._splitSymbols[i]._symbolMultiplierValue = Mental.model.gameData.deadMultiplierPlacedValue[reelId][symbolId][i];
                    symbol._splitSymbols[i].showSymbolMultiplier();
                }
            }
        }
    }
}